import {observer} from 'mobx-react-lite';
import {userCanSendTemplate} from '@verdocs/js-sdk';
import React, {FC, useEffect, useState} from 'react';
import {DocumentIcon, XMarkIcon} from '@heroicons/react/24/solid';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import {VerdocsEnvelopeRecipientSummary} from '@verdocs/web-sdk-react';
import {VerdocsPreview, VerdocsSend, VerdocsTemplateAttachments} from '@verdocs/web-sdk-react';
import {BreadcrumbTabs, Button, NonScrollingView, Spinner} from '../../Components';
import {TemplateFieldsEditor} from './TemplateComponents/TemplateFieldsEditor';
import {EditTemplateDialog, TemplateInfoDialog} from '../../Dialogs';
import {invalidateQuery, useContrast, useTemplate} from '../../react-sdk/queries';
import {TemplateRolesEditor} from './TemplateComponents/RolesEditor';
import {track} from '../../Managers/Analytics';
import {AppState} from '../../AppState';
import {URLS} from '../../Routing';

export const TemplateDetail: FC = observer(() => {
  const {id = ''} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const templateQuery = useTemplate(id);
  const contrast = useContrast();
  const [activeTab, setActiveTab] = useState(location.hash?.replace('#', '').trim() || 'preview');
  const [sending, setSending] = useState(false);
  const [doneSending, setDoneSending] = useState<null | string>(null);

  const template = templateQuery.data;
  const hasAttachments = (template?.documents?.length || 0) > 0;
  const hasRoles = (template?.roles?.length || 0) > 0;
  const isSendable = template?.is_sendable || false;

  useEffect(() => {
    if (template) {
      if (!hasAttachments && activeTab !== 'attachments') {
        handleChangeTab('attachments');
      }

      if (!hasRoles && activeTab !== 'roles' && activeTab !== 'attachments') {
        handleChangeTab('roles');
      }

      if (!userCanSendTemplate(AppState.currentProfile, template) && activeTab !== 'fields') {
        handleChangeTab('fields');
      }
    }
  }, [template, activeTab, hasAttachments, hasRoles, isSendable]);

  const invalidateTemplateData = () => {
    console.log('Invalidating template data');
    templateQuery.refetch();
  };

  const handleSend = (e: any) => {
    setSending(false);
    track('envelope_sent', {envelope_id: e.detail.envelope_id, template_id: e.detail.template_id || id});
    templateQuery.refetch();
    invalidateQuery(['envelopes']);
    setDoneSending(e.detail.envelope_id);
  };

  // The send embed fires beforeSend as it starts the request. We clear it ourselves.
  const handleSetSending = () => {
    setSending(true);
  };

  const tabs = [
    {
      id: 'attachments',
      label: 'Attachments',
    },
    {
      id: 'roles',
      label: 'Workflow',
      disabled: !hasAttachments,
    },
    {
      id: 'fields',
      label: 'Fields',
      disabled: !hasAttachments || !hasRoles,
    },
    {
      id: 'preview',
      label: 'Preview & Send',
      disabled: !hasAttachments || !hasRoles || !isSendable,
    },
  ];

  const handleChangeTab = (tab: string) => {
    window.location.hash = tab;
    setActiveTab(tab);
  };

  const handleRefresh = () => {
    // TODO: This isn't working.
    window.location.reload();
    // invalidateQuery(['templates']);
    // return templateQuery.refetch();
  };

  return (
    <NonScrollingView style={{padding: 0, position: 'relative'}}>
      <div className="w-full text-white flex z-50 flex-[0_0_56px] px-5 gap-4 items-center flex-row bg-[#33354c] shadow border-l  border-l-gray-800">
        <DocumentIcon className="w-6 h-6 tablet:w-6 tablet:h-6" />
        <div className="text-lg">{template?.name}</div>

        <div className="flex-1" />

        {template && <EditTemplateDialog template={template} onSave={handleRefresh} />}
        {template && <TemplateInfoDialog template={template} />}

        <div className="cursor-pointer" onClick={() => navigate(-1)}>
          <XMarkIcon className="w-6 h-6 tablet:w-8 tablet:h-8 opacity-70 hover:opacity-100" />
        </div>
      </div>

      <BreadcrumbTabs tabs={tabs} activeTab={activeTab} onSelect={handleChangeTab} />

      {activeTab === 'attachments' && (
        <div className="flex flex-col items-center justify-center">
          <div className="flex flex-col pt-6 pl-4 flex-none w-[340px] laptop:w-[450px] shadow bg-white p-4 mb-8 mt-8">
            <VerdocsTemplateAttachments templateId={id} onExit={() => navigate(-1)} />
          </div>
          <div className="mt-4 flex flex-1 items-start justify-start w-[340px] laptop:w-[450px]">
            <Button size="small" label="Next" onClick={() => handleChangeTab('roles')} disabled={!hasAttachments} />
          </div>
        </div>
      )}

      {activeTab === 'roles' && (
        <div className="flex flex-col items-center justify-center">
          <div className="flex flex-col pt-6 flex-none w-[340px] tablet:w-[600px] laptop:w-[768px] shadow bg-white p-6 mb-8 mt-8">
            {template?.roles !== undefined && (
              <TemplateRolesEditor templateId={id} roles={template.roles || []} onRolesUpdated={invalidateTemplateData} />
            )}
            <div className="mt-4">
              <Button size="small" label="Next" onClick={() => handleChangeTab('fields')} disabled={!hasRoles} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'loading' && (
        <div className="flex flex-1 w-full h-full pt-8 items-center justify-center">{/*<Spinner size="huge" />*/}</div>
      )}

      {activeTab === 'fields' && <TemplateFieldsEditor templateId={id} onTemplateUpdated={invalidateTemplateData} />}

      {activeTab === 'preview' && (
        <div className="flex flex-col laptop:flex-row">
          <div className="flex flex-col pt-4 pl-4 flex-none laptop:flex-[0_0_300px]">
            <VerdocsSend templateId={id} onSend={handleSend} onBeforeSend={handleSetSending} onSdkError={() => setSending(false)} />
          </div>

          <div className="flex flex-1 flex-col h-[calc(100vh-106px)] overflow-y-scroll">
            <div className="flex flex-1 max-w-[1024px] w-full border-box">
              <VerdocsPreview style={{flex: 1}} templateId={id} contrast={contrast} />
            </div>
          </div>
        </div>
      )}

      {doneSending && (
        <VerdocsEnvelopeRecipientSummary
          envelopeId={doneSending}
          onView={() => {
            setDoneSending(null);
            navigate(URLS.ENVELOPE_DETAIL.build(doneSending));
          }}
          onAnother={() => {
            setDoneSending(null);
          }}
          onNext={() => {
            setDoneSending(null);
            navigate(URLS.DASHBOARD.build());
          }}
        />
      )}

      {sending && (
        <div className="absolute top-0 left-0 w-full h-full z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Spinner size={64} />
        </div>
      )}
    </NonScrollingView>
  );
});
